import classNames from 'clsx';
import React, { type PropsWithChildren, forwardRef, useCallback, useMemo, useRef } from 'react';
import type { EViewMode } from 'reactApp/modules/settings/settings.types';
import { AnimatedScale } from 'reactApp/ui/AnimatedScale/AnimatedScale';
import { createGaSender } from 'reactApp/utils/ga';

import styles from './DataListControl.css';

const sendGa = createGaSender('datalist-control');

export type DataListControlType = 'download' | 'publish' | 'seo' | 'check' | 'restore' | 'remove' | 'downloadAsJpeg' | 'share' | 'favorite';

interface DataListControlProps extends React.HTMLAttributes<HTMLDivElement> {
    viewType: EViewMode;
    type: DataListControlType;
    animate?: boolean;
    itemId?: string;
}

const handleDoubleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
};

export const DataListControl = forwardRef<HTMLDivElement, PropsWithChildren<DataListControlProps>>(function DataListControl(
    { children, viewType, type, animate = false, onClick, itemId, ...props },
    ref
) {
    const handleClick: React.MouseEventHandler<HTMLDivElement> = useCallback(
        (e): void => {
            if (onClick) {
                e.stopPropagation();
                e.preventDefault();
                e.nativeEvent.stopImmediatePropagation();
                sendGa('click', type, viewType);

                onClick(e);
            }
        },
        [onClick, type, viewType]
    );

    const animationRef = useRef<HTMLDivElement | null>(null);

    const content = useMemo(() => {
        if (animate) {
            return (
                <div className={styles.animationWrapper} ref={animationRef}>
                    <AnimatedScale target={animationRef} scale={1.125}>
                        {children}
                    </AnimatedScale>
                </div>
            );
        }

        return children;
    }, [animate, children]);

    return (
        <div
            data-item-id={itemId}
            onDoubleClick={handleDoubleClick}
            {...props}
            className={classNames(props.className, styles.root)}
            onClick={handleClick}
            ref={ref}
        >
            {content}
        </div>
    );
});
